import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { notifications } from "@mantine/notifications";
import { DateValue, TimePicker } from "@mantine/dates";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import dayjs from "dayjs";
import {
  useGetCollectionSlots,
  useGetCollectionSuggestion,
  useGetContainerDrivers,
  postScheduleCollection,
} from "@/services/LogisticsCoordinator";
import { FullContainerOverview } from "@/types";
import { ErrorNotificationObj, SuccessNotificationObj } from "@/utils/NotificationUtil";
import { isSlotSelectable } from "@/utils/CollectionSlots";
import Text from "@/components/atoms/Text";
import DatePickerInput from "@/components/atoms/DatePickerInput";
import SVGAsset from "@/components/atoms/SVGAsset/SVGAsset";
import { Avatar, SectionTitle, initialsOf } from "../shared";
import CollectionSlotSection from "../CollectionSlotSection";
import styles from "./styles.module.css";
import shared from "../styles.module.css";

// After scheduling succeeds, keep the confirmation on screen briefly before the
// full-containers list refetches (which drops this container and closes the panel).
const REFRESH_DELAY_MS = 3000;

const DEFAULT_TIME = "08:00";
/** Mantine's TimePicker emits partial values while typing — only query on a complete HH:mm. */
const COMPLETE_TIME = /^\d{2}:\d{2}$/;

/**
 * Collections detail panel. Pick a date and an arrival time, then a driver — selecting a driver
 * creates the scheduled collection in a single call (slot or time + driver). Any later change is
 * done from the Scheduled view. Mounted with `key={containerId}` by the parent, so it resets per
 * container.
 *
 * Two modes, decided by `slotBookingRequired` on the suggestion: the site either publishes reception
 * slots that the pickup has to fit into, or it has no slot configuration and the arrival time is
 * supplied by hand.
 */
const CollectionDetail: FC<{ container: FullContainerOverview }> = ({ container }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  // Left empty until the suggestion lands, which then seeds it. Defaulting to today instead would
  // let the slots query fire for today and again for the suggested day — two fetches, one wasted.
  const [chosenDate, setChosenDate] = useState<DateValue | undefined>(undefined);
  const [chosenTime, setChosenTime] = useState(DEFAULT_TIME);
  const [selectedSlotId, setSelectedSlotId] = useState<number | null>(null);
  const [scheduledDriver, setScheduledDriver] = useState<string | null>(null);
  const refreshTimer = useRef<number | undefined>(undefined);

  useEffect(() => () => window.clearTimeout(refreshTimer.current), []);

  const dayISO = chosenDate ? dayjs(chosenDate).format("YYYY-MM-DD") : "";
  const scheduled = scheduledDriver !== null;

  const { data: suggestion, isPending: suggestionPending } = useGetCollectionSuggestion(container.containerId);
  const slotMode = suggestion?.slotBookingRequired ?? false;

  // Preselect the backend's suggested date, time and slot once the suggestion lands. Falls back to
  // today when the backend has no suggestion to offer.
  useEffect(() => {
    if (!suggestion) return;
    setChosenDate(suggestion.suggestedDateTime ? new Date(suggestion.suggestedDateTime) : new Date());
    if (suggestion.suggestedDateTime) {
      setChosenTime(dayjs(suggestion.suggestedDateTime).format("HH:mm"));
    }
    setSelectedSlotId(suggestion.suggestedReceptionSlotId ?? null);
  }, [suggestion]);

  // Slot mode: the slots for the picked day carry their own free drivers, so there is no driver call.
  const { data: slotData, isFetching: slotsFetching } = useGetCollectionSlots(container.containerId, dayISO, dayISO, {
    enabled: slotMode && !!dayISO && !scheduled,
  });
  const slots = slotData?.slots ?? [];
  const selectedSlot = slots.find((entry) => entry.slot.id === selectedSlotId) ?? null;

  // Non-slot mode: we supply the window ourselves, so drivers are refetched on every time change.
  const plannedDateISO =
    !slotMode && dayISO && COMPLETE_TIME.test(chosenTime) ? dayjs(`${dayISO}T${chosenTime}`).toISOString() : "";
  // Wait for the suggestion before asking: until it lands `slotMode` reads false, and this would
  // fire a driver lookup at a slot-booking site — the wrong question, against the wrong endpoint.
  const { data: driverData } = useGetContainerDrivers(container.containerId, plannedDateISO, {
    enabled: !suggestionPending && !slotMode && !!plannedDateISO && !scheduled,
  });

  const drivers = slotMode ? (selectedSlot?.availableDrivers ?? []) : (driverData?.drivers ?? []);
  const fleetSize = (slotMode ? slotData?.fleetSize : driverData?.fleetSize) ?? 0;
  // The suggestion can preselect a slot that has since passed or filled up — re-check, don't trust it.
  const timeChosen = slotMode ? !!selectedSlot && isSlotSelectable(selectedSlot) : !!plannedDateISO;

  const handleDateChange = (value: DateValue | undefined) => {
    setChosenDate(value);
    // A slot id belongs to one day — drop it rather than booking yesterday's slot.
    setSelectedSlotId(null);
  };

  // Picking a driver creates the scheduled collection (slot/time + driver in one call).
  const scheduleCollection = useMutation({
    mutationFn: (driverUserId: number) =>
      postScheduleCollection({
        containerId: container.containerId,
        driverUserId,
        ...(slotMode && selectedSlotId !== null
          ? { receptionSlotId: selectedSlotId }
          : { plannedDate: plannedDateISO }),
      }),
    onSuccess: (_res, driverUserId) => {
      const name = drivers.find((d) => d.userId === driverUserId)?.name ?? "";
      setScheduledDriver(name);
      notifications.show(
        SuccessNotificationObj(t("notifications:generic.success"), t("containers:toast.driverAssigned", { name }))
      );
      // The booking consumed slot capacity and added a collection to the schedule.
      queryClient.invalidateQueries({ queryKey: ["collectionSlots"] });
      queryClient.invalidateQueries({ queryKey: ["getCollections"] });
      // Delay the list refresh so the confirmation stays visible for a moment.
      refreshTimer.current = window.setTimeout(
        () => queryClient.invalidateQueries({ queryKey: ["getFullContainers"] }),
        REFRESH_DELAY_MS
      );
    },
    onError: () => {
      notifications.show(ErrorNotificationObj(t("notifications:generic.error"), t("containers:toast.assignError")));
    },
  });

  return (
    <aside className={shared.detailPanel}>
      <div className={shared.detailHeader}>
        {/* TODO: waste-type tile (abbreviation) — no abbr field from the API yet. */}
        <div className={shared.detailHeaderText}>
          <Text size="sm">
            {container.wasteTypeName} · {container.siteName}
          </Text>
          <Text display="technical" size="xs" tt="uppercase" color="tertiary" mt={6}>
            {t("containers:detail.reported", {
              date: dayjs(container.reported).format("DD MMM"),
              abs: dayjs(container.reported).format("HH:mm"),
            })}
          </Text>
        </div>
      </div>

      <div className={shared.detailBody}>
        <SectionTitle>{t("containers:detail.scheduleCollection")}</SectionTitle>

        <div className={shared.editPanel} style={{ marginTop: 14 }}>
          <DatePickerInput
            label={t("containers:detail.pickDate")}
            placeholder={t("actions:selectDate")}
            value={chosenDate}
            onChange={handleDateChange}
            minDate={new Date()}
            disabled={scheduled || scheduleCollection.isPending}
          />

          {suggestion?.deadlineDate && (
            <Text display="technical" size="xs" tt="uppercase" color="tertiary" mt={8}>
              {t("containers:detail.deadline", { date: dayjs(suggestion.deadlineDate).format("D MMM") })}
            </Text>
          )}

          {/* The site's arrival windows, or a free time entry when it publishes none. */}
          {!scheduled && slotMode && (
            <CollectionSlotSection
              slots={slots}
              value={selectedSlotId}
              onChange={setSelectedSlotId}
              loading={slotsFetching || suggestionPending}
              disabled={scheduleCollection.isPending}
            />
          )}

          {!scheduled && !slotMode && !suggestionPending && (
            <div style={{ marginTop: 16 }}>
              <SectionTitle>{t("containers:detail.pickTime")}</SectionTitle>
              <div style={{ marginTop: 10 }}>
                <TimePicker
                  format="24h"
                  value={chosenTime}
                  onChange={setChosenTime}
                  disabled={scheduleCollection.isPending}
                />
              </div>
            </div>
          )}
        </div>

        <div className={shared.divider}>
          <div className={shared.detailSectionHeader}>
            <SectionTitle>{t("containers:detail.assignDriver")}</SectionTitle>
            {!scheduled && timeChosen && (
              <Text span display="technical" size="xs" tt="uppercase" color="secondary">
                {t("containers:detail.fleetMeta", { available: drivers.length, total: fleetSize })}
              </Text>
            )}
          </div>

          {!scheduled && !timeChosen && (
            <div className={shared.sectionNote}>
              <Text size="sm" color="tertiary">
                {slotMode ? t("containers:detail.slotDriverLocked") : t("containers:detail.driverLocked")}
              </Text>
            </div>
          )}

          {!scheduled && timeChosen && (
            <div className={shared.driverList} style={{ marginTop: 12 }}>
              {drivers.map((d) => (
                <button
                  key={d.userId}
                  type="button"
                  disabled={scheduleCollection.isPending}
                  onClick={() => scheduleCollection.mutate(d.userId)}
                  className={shared.driverRow}
                >
                  <Avatar initials={initialsOf(d.name)} size={34} />
                  <span className={shared.driverMeta}>
                    <span className={shared.driverName}>
                      <Text span size="sm" strong>
                        {d.name}
                      </Text>
                    </span>
                  </span>
                  <Text span display="technical" size="xs" tt="uppercase" color="success">
                    {t("containers:driver.available")}
                  </Text>
                </button>
              ))}
              {drivers.length === 0 && (
                <div className={shared.sectionNote}>
                  <Text size="sm" color="tertiary">
                    {t("containers:detail.noDrivers")}
                  </Text>
                </div>
              )}
            </div>
          )}

          {scheduled && (
            <div className={styles.assignedCard} style={{ marginTop: 12 }}>
              <div className={styles.assignedTop}>
                <Avatar initials={initialsOf(scheduledDriver ?? "")} size={38} />
                <div style={{ flex: 1 }}>
                  <Text size="md" strong>
                    {scheduledDriver}
                  </Text>
                </div>
              </div>
              <div className={styles.assignedFooter}>
                <SVGAsset name="checkmark-green" width={15} height={15} />
                <Text size="sm" color="success">
                  {t("containers:detail.collectionScheduled")}
                </Text>
              </div>
            </div>
          )}
        </div>
      </div>
    </aside>
  );
};

export default CollectionDetail;
